import { WorkOrder } from '../../../../entities/job/workorder';
import { WorkOrderStatusSystemName } from '../../../../entities/job/workorder.status.system.name';
import { ActivityEntityName } from '../../../../entities/shared/activity.entity.name';
import { EntitySecurityCapabilityRegistry } from '../../../entity.security.capability.registry';
import { JobSecurityRight } from '../../../modules/job.security.module';
import { EntitySecurityFacts } from '../../../entity.security.facts';
import { jobSecurityHasRight } from '../job.security.has.right';
import { StageSecurityCapabilities } from './stage.security.capabilities';
import { stageIsClosedOrCancelled, stageIsCompletedOrLater, stageIsFinalised } from './stage.security.state';

EntitySecurityCapabilityRegistry.register<WorkOrder, StageSecurityCapabilities>(ActivityEntityName.WorkOrder, getStageSecurityCapabilities);

export function getStageSecurityCapabilities(facts: EntitySecurityFacts<WorkOrder>): StageSecurityCapabilities {

    const stage = facts.before ?? facts.entity ?? facts.after;
    const role = facts.role;
    const canManage = jobSecurityHasRight(role, JobSecurityRight.Manage);
    const canModifyFinalised = jobSecurityHasRight(role, JobSecurityRight.ModifyJobAfterFinalise);

    return {
        canManage,
        // Closed/Cancelled stages are terminal and not editable (matches the UI, which makes
        // them read-only with no override). Finalised stays editable only with the
        // ModifyJobAfterFinalise right.
        canPatch: canManage && !stageIsClosedOrCancelled(stage) && (!stageIsFinalised(stage) || canModifyFinalised),
        canDelete: canManage && (!stageIsFinalised(stage) || canModifyFinalised),
        canDispatch: jobSecurityHasRight(role, JobSecurityRight.Dispatch),
        canReissue: jobSecurityHasRight(role, JobSecurityRight.Dispatch) && stageCanReissue(stage),
        canProcessStatus: jobSecurityHasRight(role, JobSecurityRight.ProcessWorkOrder),
        canReverseStatus: jobSecurityHasRight(role, JobSecurityRight.ReverseWorkOrderStatus),
        canFinalise: jobSecurityHasRight(role, JobSecurityRight.FinalliseWorkOrder),
        canViewPricing: jobSecurityHasRight(role, JobSecurityRight.ViewPricing, JobSecurityRight.ModifyPricing),
        canModifyPricing: jobSecurityHasRight(role, JobSecurityRight.ModifyPricing),
        canModifyFinalised,
        canModifyCompletionDate: stageIsCompletedOrLater(stage) && jobSecurityHasRight(role, JobSecurityRight.CompletionDate),
        canModifyFinaliseDate: stageIsFinalised(stage) && jobSecurityHasRight(role, JobSecurityRight.FinaliseDate),
        canMarkFinanceLock: jobSecurityHasRight(role, JobSecurityRight.MarkFinanceLock),
    };
}

function stageCanReissue(stage?: WorkOrder): boolean {

    // Reissue resends the dispatch notification, so it only makes sense once the stage
    // has actually been dispatched. New/Assigned stages have never been dispatched (the
    // resource has not been notified of the work yet), and Canceled/Closed stages are no
    // longer actionable — none of these can be reissued.
    const blockedStatuses = [WorkOrderStatusSystemName.New, WorkOrderStatusSystemName.Assigned, WorkOrderStatusSystemName.Canceled, WorkOrderStatusSystemName.Closed];

    return !blockedStatuses.includes(stage?.status?.systemName as WorkOrderStatusSystemName);
}
